import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { CancelButton } from "../Buttons/CancelButton";
import { cond, isNil, T } from "ramda";
import currency from "currency.js";
import { EjiDiscount } from "../../generated/nest-graphql";
import { NumberFormatCustom } from "../Forms/fields/CurrencyInput";
import {
  DISCOUNT_LIMIT_FLAT,
  DISCOUNT_LIMIT_PERCENT,
  DISCOUNT_REASONS,
  EJI_DISCOUNT_TYPE_FLAT,
  EJI_DISCOUNT_TYPE_PERCENT,
  EJI_DISCOUNT_TYPE_PACKAGE,
} from "../../lib/constants";
import { useQuery } from "@apollo/client";
import { GET_ACTIVE_PACKAGE_DISCOUNTS } from "../../graphql/queries/getActivePackageDiscounts";

type DiscountModalTypes = {
  existingDiscount?: EjiDiscount;
  discountIdx?: number;
  closeModal: any;
  onSubmit: any;
};

const DiscountModal = ({
  existingDiscount = { type: null, reason: null, amount: null, total: null },
  closeModal,
  onSubmit,
}: DiscountModalTypes) => {
  const { data: packageDiscountsData } = useQuery(GET_ACTIVE_PACKAGE_DISCOUNTS);

  const typeOptions = [
    { value: EJI_DISCOUNT_TYPE_PERCENT, label: "Percent discount" },
    { value: EJI_DISCOUNT_TYPE_FLAT, label: "Flat dollar discount" },
    { value: EJI_DISCOUNT_TYPE_PACKAGE, label: "Package discount" },
  ];
  const reasonOptions = DISCOUNT_REASONS.map((reasonText) => ({
    value: reasonText,
    label: reasonText,
  }));
  const [discount, setDiscount] = useState({
    ...existingDiscount,
    amount:
      existingDiscount.type === EJI_DISCOUNT_TYPE_FLAT
        ? currency(existingDiscount?.total).value
        : existingDiscount.amount,
  });
  const [submitted, setSubmitted] = useState(false);
  const invalidFlatDiscountAmount = discount.type === EJI_DISCOUNT_TYPE_FLAT && discount.amount > DISCOUNT_LIMIT_FLAT;
  const invalidPercentDiscountAmount =
    discount.type === EJI_DISCOUNT_TYPE_PERCENT && discount.amount > DISCOUNT_LIMIT_PERCENT;
  const invalidDiscountAmount =
    invalidFlatDiscountAmount ||
    invalidPercentDiscountAmount ||
    (discount.type === EJI_DISCOUNT_TYPE_PACKAGE && !discount.reason);
  const invalidDiscount =
    !discount.type ||
    (discount.type !== EJI_DISCOUNT_TYPE_PACKAGE && (isNil(discount.amount) || discount.amount < 0)) ||
    !discount.reason ||
    invalidDiscountAmount;
  const flatAmountText = discount.amount ? "Amount" : "Enter discount amount";
  const percentAmountText = discount.amount ? "Percent" : "Enter discount percent";

  const packageOptions =
    packageDiscountsData?.getActivePackageDiscounts?.map((pkg: any) => ({
      value: pkg.name,
      label: pkg.name,
    })) ?? [];

  const selectedPackage =
    packageDiscountsData?.getActivePackageDiscounts?.find((pkg: any) => pkg.name === discount.reason) ?? null;
  return (
    <div className="flex flex-col justify-between gap-4">
      <FormControl fullWidth variant="outlined" required>
        <InputLabel>Type</InputLabel>
        <Select
          label={"Type"}
          onChange={(event) => {
            const discountType = event.target.value.toString();
            setDiscount((currentDiscount) => {
              const tempDiscount = { ...currentDiscount };
              tempDiscount.type = discountType;
              if (discountType === EJI_DISCOUNT_TYPE_PACKAGE) {
                tempDiscount.amount = null;
              }
              return tempDiscount;
            });
          }}
          value={discount.type}
        >
          {typeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {discount.type !== EJI_DISCOUNT_TYPE_PACKAGE && (
        <TextField
          error={invalidDiscountAmount}
          helperText={
            invalidDiscountAmount
              ? cond([
                  [
                    () => invalidPercentDiscountAmount,
                    () => `Discount cannot be greater than ${DISCOUNT_LIMIT_PERCENT}%`,
                  ],
                  [() => invalidFlatDiscountAmount, () => `Discount cannot be greater than $${DISCOUNT_LIMIT_FLAT}`],
                  [T, () => ""],
                ])()
              : ""
          }
          FormHelperTextProps={{
            style: { color: "red" },
          }}
          fullWidth
          variant={"outlined"}
          label={discount.type === EJI_DISCOUNT_TYPE_PERCENT ? percentAmountText : flatAmountText}
          onChange={(event) => {
            const amount = event.target.value === "" ? null : Number(event.target.value);
            setDiscount((currentDiscount) => {
              const tempDiscount = { ...currentDiscount };
              tempDiscount.amount = amount;
              return tempDiscount;
            });
          }}
          required
          disabled={!discount.type}
          value={discount.amount}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            endAdornment: (
              <InputAdornment position="end" className="text-xs pr-0">
                {!!discount.type && (discount.type === EJI_DISCOUNT_TYPE_FLAT ? "$" : "%")}
              </InputAdornment>
            ),
          }}
        />
      )}
      {discount.type === EJI_DISCOUNT_TYPE_PACKAGE && (
        <FormControl fullWidth variant="outlined" required>
          <InputLabel>Package</InputLabel>
          <Select
            label={"Package"}
            onChange={(event) => {
              const reason = event.target.value.toString();
              setDiscount((currentDiscount) => {
                const tempDiscount = { ...currentDiscount };
                tempDiscount.reason = reason;
                return tempDiscount;
              });
            }}
            value={discount.reason}
          >
            {packageOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {discount.type === EJI_DISCOUNT_TYPE_PACKAGE && discount.reason && (
        <Box className="mt-4 mb-4 p-4 border rounded">
          <Typography variant="subtitle2" className="mb-2 font-bold">
            Package Discount Details and Conditions:
          </Typography>
          <Typography variant="body2" className="mb-1">
            Package: {discount.reason}
          </Typography>
          {selectedPackage?.rules && Array.isArray(selectedPackage.rules) && selectedPackage.rules.length > 0 && (
            <Box className="mt-2">
              <Typography variant="body2" className="mb-1 font-semibold">
                Conditions:
              </Typography>
              <ul className="list-disc list-inside ml-2">
                {selectedPackage.rules.map((rule: any, idx: number) => {
                  if (!rule) return null;

                  const whenConditions = Array.isArray(rule.when) ? rule.when : [];
                  const ruleDiscount = rule.discount || {};

                  return (
                    <li key={idx} className="text-sm mb-1">
                      {whenConditions.length > 0 ? (
                        <span>
                          {whenConditions.map((when: any, whenIdx: number) => {
                            const field = when?.field || "N/A";
                            const operator = when?.operator || "";
                            const value = when?.value || (Array.isArray(when?.values) ? when.values.join(", ") : "");
                            return (
                              <span key={whenIdx}>
                                {whenIdx > 0 && " AND "}
                                {field} {operator} {value}
                              </span>
                            );
                          })}
                          {ruleDiscount.type && (
                            <span className="ml-2 text-green-600">
                              → Discount: {ruleDiscount.value} {ruleDiscount.type}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span>
                          No conditions specified
                          {ruleDiscount.type && (
                            <span className="ml-2 text-green-600">
                              → Discount: {ruleDiscount.value} {ruleDiscount.type}
                            </span>
                          )}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Box>
          )}
        </Box>
      )}
      {discount.type !== EJI_DISCOUNT_TYPE_PACKAGE && (
        <FormControl fullWidth variant="outlined" required>
          <InputLabel>Reason</InputLabel>
          <Select
            label={"Reason"}
            onChange={(event) => {
              const reason = event.target.value.toString();
              setDiscount((currentDiscount) => {
                const tempDiscount = { ...currentDiscount };
                tempDiscount.reason = reason;
                return tempDiscount;
              });
            }}
            value={discount.reason}
          >
            {reasonOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <div className="grid grid-rows-1 grid-cols-3 col-span-3 gap-4">
        <div className="justify-items-start">
          <div className="justify-self-stretch flex flex-col">
            <CancelButton onClick={closeModal}>Cancel</CancelButton>
          </div>
        </div>
        <div className="justify-items-start col-start-3">
          <div className="justify-self-stretch flex flex-col">
            <Button
              disabled={invalidDiscount || submitted}
              style={{ fontWeight: "bolder" }}
              size={"large"}
              variant={"contained"}
              color={"primary"}
              onClick={() => {
                setSubmitted(true);

                const submitData: any = { ...discount };

                if (discount.type === EJI_DISCOUNT_TYPE_FLAT) {
                  submitData.amount = currency(discount.amount).intValue;
                } else if (discount.type === EJI_DISCOUNT_TYPE_PACKAGE && selectedPackage?.id) {
                  submitData.amount = 0;
                  submitData.total = currency(0).intValue;
                  submitData.id = selectedPackage.id;
                }

                onSubmit(submitData);
              }}
            >
              submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountModal;
